import solutionsRaw from './wordpop-solutions-clean.txt?raw';
import { encodeWordlist, decodeWordlist } from '@/utils/wordlistObfuscation';
import { BLOCKLIST } from './blocklist';

// Seeded PRNG for deterministic chunk shuffling (ensures all users get same word order)
function seededRandom(seed: number): () => number {
  let state = seed;
  return function() {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

// Deterministic shuffle using seeded PRNG (NOT Math.random!)
function deterministicShuffle<T>(array: T[], seed: number): T[] {
  const result = [...array];
  const rng = seededRandom(seed);
  
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  
  return result;
}

// Fixed seed for obfuscation shuffle (deterministic across all clients)
const OBFUSCATION_SEED = 0x574F524D; // "WORM" in hex

// Process the WordPop solution words
const processWordList = (rawText: string): string[] => {
  const blocklistSet = new Set(BLOCKLIST.map(w => w.toUpperCase()));
  
  return rawText
    .split('\n')
    .map(word => word.trim().toUpperCase())
    .filter(word => 
      word.length === 5 && 
      /^[A-Z]+$/.test(word) &&
      !blocklistSet.has(word)  // Filter out blocklisted words
    );
};

// Process and obfuscate word list
const rawWords = processWordList(solutionsRaw);
const { chunks, hash, noise } = encodeWordlist(rawWords);

// Store obfuscated data (chunks + noise mixed together to confuse scrapers)
// CRITICAL: Use deterministic shuffle (NOT Math.random!) to ensure all users get same word order
const obfuscatedData = {
  c: deterministicShuffle([...chunks, ...noise], OBFUSCATION_SEED),
  h: hash,
  v: chunks.length // Real chunk count
};

// Lazy decode on first access
let decodedWords: string[] | null = null;

function getDecodedWords(): string[] {
  if (!decodedWords) {
    // Extract real chunks using STABLE sort (length descending, then original index)
    // CRITICAL: Index tiebreaker ensures deterministic ordering across all clients
    const sortedChunks = obfuscatedData.c
      .slice()
      .map((chunk, index) => ({ chunk, index }))
      .sort((a, b) => {
        // Primary: sort by length descending
        const lenDiff = b.chunk.length - a.chunk.length;
        if (lenDiff !== 0) return lenDiff;
        // Secondary: stable tiebreaker using original index
        return a.index - b.index;
      })
      .slice(0, obfuscatedData.v)
      .map(item => item.chunk);
    
    decodedWords = decodeWordlist(sortedChunks, obfuscatedData.h);
  }
  return decodedWords;
}

// Export solution words via lazy getter
export const solutionWords = getDecodedWords();
export default solutionWords;

// Export obfuscation info for security scans
export const obfuscationInfo = {
  isObfuscated: true,
  hash: obfuscatedData.h,
  chunkCount: obfuscatedData.v
};